import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartBarIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import { UserContext } from '../context/UserContext';
import { getPeriodComparison } from '../services/api';
import { toast } from 'react-hot-toast';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell, Button, CustomSelect } from '@redpanda/ui';
import { Role } from '@redpanda/types';
import usePeriodComparisonStore from '../stores/periodComparisonStore';

interface Period {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  closingTypeLabel?: string;
  fiscalYear: {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
  };
}

interface ComparisonData {
  periods: Period[];
  accounts: Array<{
    id: string;
    code: string;
    name: string;
    type: string;
    level: number;
    periodBalances: Array<{
      periodId: string;
      debitTotal: number;
      creditTotal: number;
      balance: number;
      previousBalance?: number;
      change?: number;
      changePercent?: number;
    }>;
  }>;
  summary: {
    totalsByPeriod: Array<{
      periodId: string;
      totalAssets: number;
      totalLiabilities: number;
      totalEquity: number;
      totalRevenue: number;
      totalExpenses: number;
      netIncome: number;
    }>;
  };
}

const PeriodComparisonPage: React.FC = () => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const { user } = userContext!;
  const userRole = user?.role;

  // 使用新的 periodComparisonStore
  const {
    availablePeriods,
    error: storeError,
    fetchAvailablePeriods
  } = usePeriodComparisonStore();

  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);
  const [reportType, setReportType] = useState<'TRIAL_BALANCE' | 'INCOME_STATEMENT' | 'BALANCE_SHEET'>('TRIAL_BALANCE');
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const canView = userRole && [Role.SUPER_ADMIN, Role.ADMIN, Role.USER].includes(userRole);

  useEffect(() => {
    if (canView) {
      fetchAvailablePeriods(true); // 進入頁面時強制刷新
    }
  }, [canView]);


  const handleGenerateComparison = async () => {
    if (selectedPeriods.length < 2) {
      toast.error(t('periodComparison.selectAtLeastTwo'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await getPeriodComparison({
        periodIds: selectedPeriods,
        reportType,
        includeSubAccounts: true
      });

      setComparisonData(response.data);
      toast.success(t('periodComparison.generateSuccess'));
    } catch (error: any) {
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          t('periodComparison.generateError');
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodToggle = (periodId: string) => {
    setSelectedPeriods(prev => {
      if (prev.includes(periodId)) {
        return prev.filter(id => id !== periodId);
      } else if (prev.length < 6) {
        return [...prev, periodId];
      } else {
        toast.error(t('periodComparison.maxSixPeriods'));
        return prev;
      }
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (percent: number) => {
    if (!isFinite(percent)) return '-';
    return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-TW');
  };

  const getReportTypeLabel = () => {
    return t(`periodComparison.reportType.${reportType}`, t('periodComparison.comparisonReport'));
  };

  const getChangeIndicator = (change: number | undefined, changePercent: number | undefined) => {
    if (change === undefined || changePercent === undefined) return null;
    
    const isPositive = change > 0;
    const isNegative = change < 0;
    
    return (
      <div className="flex items-center space-x-1">
        <span className={`text-sm ${isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-500'}`}>
          {formatCurrency(change)}
        </span>
        <span className={`text-xs ${isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-500'}`}>
          ({formatPercentage(changePercent)})
        </span>
      </div>
    );
  };


  if (!canView) {
    return (
      <div className="container mx-auto px-10 py-6 pb-32">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('periodComparison.noAccess')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('periodComparison.noAccessMessage')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('periodComparison.pageTitle')}</h1>
          {/* 隱藏按鈕保持高度一致 */}
          <div className="opacity-0 pointer-events-none">
            <button className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200">
              {t('periodComparison.hiddenButton')}
            </button>
          </div>
        </div>
      </div>

      {/* 比較設定區域 */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{t('periodComparison.settings')}</h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('periodComparison.reportTypeLabel')}
              </label>
              <CustomSelect
                value={reportType}
                onChange={(value) => setReportType(value as any)}
                options={[
                  { value: 'TRIAL_BALANCE', label: t('periodComparison.reportType.TRIAL_BALANCE') },
                  { value: 'INCOME_STATEMENT', label: t('periodComparison.reportType.INCOME_STATEMENT') },
                  { value: 'BALANCE_SHEET', label: t('periodComparison.reportType.BALANCE_SHEET') }
                ]}
              />
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('periodComparison.selectPeriods', { selected: selectedPeriods.length, max: 6 })}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                {availablePeriods.map(period => (
                  <label
                    key={period.id}
                    className={`flex items-center p-2 rounded border cursor-pointer transition-colors ${
                      selectedPeriods.includes(period.id)
                        ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-300 dark:border-brand-600 text-brand-700 dark:text-brand-300'
                        : 'bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPeriods.includes(period.id)}
                      onChange={() => handlePeriodToggle(period.id)}
                      className="mr-2 text-brand-600 focus:ring-brand-500"
                    />
                    <div className="text-sm">
                      <div className="font-medium text-gray-900 dark:text-white">{period.fiscalYear.name}</div>
                      <div className="text-gray-500 dark:text-gray-400">
                        {period.name}
                        {period.closingTypeLabel && (
                          <span className="ml-1 text-xs px-1 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded">
                            {period.closingTypeLabel}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              onClick={handleGenerateComparison}
              disabled={loading || selectedPeriods.length < 2}
              className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  {t('periodComparison.generating')}
                </>
              ) : (
                <>
                  <ChartBarIcon className="h-4 w-4 mr-2" />
                  {t('periodComparison.generateReport')}
                </>
              )}
            </Button>
          </div>
      </div>

      {/* 錯誤訊息 */}
      {(error || storeError) && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error || storeError}
          </div>
      )}

      {/* 比較結果 */}
      {comparisonData && (
          <div className="space-y-6">
            {/* 摘要統計 */}
            <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {getReportTypeLabel()} - {t('periodComparison.summary')}
              </h2>

              <div className="overflow-x-auto">
                <Table style={{ tableLayout: 'fixed', minWidth: `${Math.max(600, comparisonData.periods.length * 150)}px` }}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-32">{t('periodComparison.item')}</TableHead>
                      {comparisonData.periods.map(period => (
                        <TableHead key={period.id} className="text-center">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{period.fiscalYear.name}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{period.name}</div>
                            <div className="text-xs text-gray-400 dark:text-gray-500">
                              {formatDate(period.startDate)} ~ {formatDate(period.endDate)}
                            </div>
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportType === 'BALANCE_SHEET' && (
                      <>
                        <TableRow>
                          <TableCell className="font-medium text-gray-900 dark:text-white">{t('periodComparison.totalAssets')}</TableCell>
                          {comparisonData.summary.totalsByPeriod.map(totals => (
                            <TableCell key={totals.periodId} className="text-right">
                              {formatCurrency(totals.totalAssets)}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium text-gray-900 dark:text-white">{t('periodComparison.totalLiabilities')}</TableCell>
                          {comparisonData.summary.totalsByPeriod.map(totals => (
                            <TableCell key={totals.periodId} className="text-right">
                              {formatCurrency(totals.totalLiabilities)}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium text-gray-900 dark:text-white">{t('periodComparison.totalEquity')}</TableCell>
                          {comparisonData.summary.totalsByPeriod.map(totals => (
                            <TableCell key={totals.periodId} className="text-right">
                              {formatCurrency(totals.totalEquity)}
                            </TableCell>
                          ))}
                        </TableRow>
                      </>
                    )}

                    {(reportType === 'INCOME_STATEMENT' || reportType === 'TRIAL_BALANCE') && (
                      <>
                        <TableRow>
                          <TableCell className="font-medium text-gray-900 dark:text-white">{t('periodComparison.totalRevenue')}</TableCell>
                          {comparisonData.summary.totalsByPeriod.map(totals => (
                            <TableCell key={totals.periodId} className="text-right">
                              {formatCurrency(totals.totalRevenue)}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium text-gray-900 dark:text-white">{t('periodComparison.totalExpenses')}</TableCell>
                          {comparisonData.summary.totalsByPeriod.map(totals => (
                            <TableCell key={totals.periodId} className="text-right">
                              {formatCurrency(totals.totalExpenses)}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow className="bg-gray-50 dark:bg-slate-700">
                          <TableCell className="font-bold text-gray-900 dark:text-white">{t('periodComparison.netIncome')}</TableCell>
                          {comparisonData.summary.totalsByPeriod.map(totals => (
                            <TableCell key={totals.periodId} className={`text-right font-bold ${
                              totals.netIncome >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatCurrency(totals.netIncome)}
                            </TableCell>
                          ))}
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* 詳細科目比較 */}
            <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('periodComparison.accountDetails')}
              </h2>

              <div className="overflow-x-auto">
                <Table style={{ tableLayout: 'fixed', minWidth: `${Math.max(800, comparisonData.periods.length * 200)}px` }}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">{t('periodComparison.accountCode')}</TableHead>
                      <TableHead className="w-48">{t('periodComparison.accountName')}</TableHead>
                      <TableHead className="w-20">{t('periodComparison.type')}</TableHead>
                      {comparisonData.periods.map((period, index) => (
                        <TableHead key={period.id} className="text-center min-w-[200px]">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{period.name}</div>
                            {index > 0 && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">{t('periodComparison.vsPrevious')}</div>
                            )}
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comparisonData.accounts
                      .filter(account => account.periodBalances.some(pb => Math.abs(pb.balance) > 0.01))
                      .map(account => (
                        <TableRow key={account.id}>
                          <TableCell className="font-mono text-sm">{account.code}</TableCell>
                          <TableCell>
                            <div style={{ paddingLeft: `${(account.level - 1) * 16}px` }}>
                              {account.name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded">
                              {account.type}
                            </span>
                          </TableCell>
                          {account.periodBalances.map((balance, index) => (
                            <TableCell key={balance.periodId} className="text-right">
                              <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {formatCurrency(balance.balance)}
                                </div>
                                {index > 0 && getChangeIndicator(balance.change, balance.changePercent)}
                              </div>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
      )}

      {/* 無期間可比較的提示 */}
      {availablePeriods.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
            {t('periodComparison.noPeriodsAvailable')}
        </div>
      )}
    </div>
  );
};

export default PeriodComparisonPage;